import { RANK_CHARS, rankOf, suitOf, type Card } from "../src/engine/cards.ts";

/** Suit shapes drawn as paths (not text glyphs) so they look identical on every device. */
const SUIT_SHAPES: string[] = [
  // clubs
  `<circle cx="12" cy="7.2" r="4.7"/><circle cx="6.4" cy="14.2" r="4.7"/><circle cx="17.6" cy="14.2" r="4.7"/><path d="M12 11c0 5-1.4 8.6-3.6 11h7.2C13.4 19.6 12 16 12 11z"/>`,
  // diamonds
  `<path d="M12 1.5 21 12l-9 10.5L3 12z"/>`,
  // hearts
  `<path d="M12 22C4.6 16 2 12.4 2 8.6A5.2 5.2 0 0 1 12 6.4 5.2 5.2 0 0 1 22 8.6C22 12.4 19.4 16 12 22z"/>`,
  // spades
  `<path d="M12 1.5C12 1.5 2.5 9 2.5 14.2a4.6 4.6 0 0 0 8.2 2.8c-.2 2.4-1.3 3.9-3.1 5h8.8c-1.8-1.1-2.9-2.6-3.1-5a4.6 4.6 0 0 0 8.2-2.8C21.5 9 12 1.5 12 1.5z"/>`,
];

const RED = "#b3202a";
const INK = "#1a1613";
const suitColor = (suit: number) => (suit === 1 || suit === 2 ? RED : INK);

const FONT = `font-family="'Times New Roman', Times, 'Liberation Serif', serif"`;

function suit(suitIdx: number, x: number, y: number, size: number): string {
  return `<g transform="translate(${x} ${y}) scale(${size / 24})" fill="${suitColor(suitIdx)}">${SUIT_SHAPES[suitIdx]}</g>`;
}

const faceCache = new Map<Card, string>();

/** Ivory card stock with classic serif indices. */
export function cardFaceSVG(card: Card): string {
  const hit = faceCache.get(card);
  if (hit) return hit;
  const r = rankOf(card);
  const su = suitOf(card);
  const rank = RANK_CHARS[r] === "T" ? "10" : RANK_CHARS[r]!;
  const col = suitColor(su);
  const rs = rank.length === 2 ? 27 : 32;
  const rx = rank.length === 2 ? 7 : 11;
  const corner =
    `<text x="${rx}" y="31" font-size="${rs}" font-weight="700" fill="${col}" ${FONT} letter-spacing="-1.5">${rank}</text>` + suit(su, 9, 37, 20);
  const svg =
    `<svg viewBox="0 0 100 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
    `<rect x="1" y="1" width="98" height="138" rx="10" fill="#f6eedb" stroke="#a89466" stroke-width="1.5"/>` +
    `<rect x="4" y="4" width="92" height="132" rx="8" fill="none" stroke="#e4d6b3" stroke-width="1"/>` +
    corner +
    suit(su, 30, 62, 40) +
    `<g transform="rotate(180 50 70)">${corner}</g></svg>`;
  faceCache.set(card, svg);
  return svg;
}

/** Card back: burgundy with a gold lattice and an ornamental centre. Pattern lives in SHARED_DEFS. */
export const CARD_BACK_SVG =
  `<svg viewBox="0 0 100 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
  `<rect x="1" y="1" width="98" height="138" rx="10" fill="#f3e9cf" stroke="#a89466" stroke-width="1.5"/>` +
  `<rect x="7" y="7" width="86" height="126" rx="5" fill="url(#wg-back)" stroke="#c9a24a" stroke-width="1.4"/>` +
  `<rect x="12" y="12" width="76" height="116" rx="3" fill="none" stroke="#c9a24a" stroke-opacity=".55" stroke-width=".8"/>` +
  `<circle cx="50" cy="70" r="15" fill="#4f0d18" stroke="#c9a24a" stroke-width="1.6"/>` +
  `<path d="M50 58l9 12-9 12-9-12z" fill="#c9a24a"/>` +
  `<circle cx="50" cy="70" r="3.2" fill="#4f0d18"/>` +
  `</svg>`;

/** Shared SVG defs (card-back pattern). Injected once into the page. */
export const SHARED_DEFS =
  `<svg class="defs" width="0" height="0" aria-hidden="true"><defs>` +
  `<pattern id="wg-back" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">` +
  `<rect width="10" height="10" fill="#741424"/><path d="M0 0h10M0 0v10" stroke="#c9a24a" stroke-opacity=".38" stroke-width=".9"/></pattern>` +
  `</defs></svg>`;

/** Subtle woven-cloth speckle laid over the felt (an in-page SVG filter, so no image files are needed). */
export const FELT_NOISE_SVG =
  `<svg class="felt-noise" width="1024" height="456" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
  `<filter id="wg-noise" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".85" numOctaves="2" seed="7" result="n"/><feColorMatrix type="saturate" values="0"/></filter>` +
  `<rect width="100%" height="100%" filter="url(#wg-noise)"/></svg>`;

/**
 * The dealer is drawn in two layers: the BACK layer (head, torso) sits underneath the table so the
 * table edge hides his lower half, and the FRONT layer (arms) reaches over the rail to deal.
 * Hooks: .d-arm-l / .d-arm-r (flick), .d-eyes (blink), .d-mouth (talk), .d-wisp (cigar smoke).
 */
export const DEALER_BACK_SVG = `
<svg viewBox="0 0 260 230" xmlns="http://www.w3.org/2000/svg" aria-label="Dealer">
  <g class="d-body">
    <path d="M62 84Q92 74 118 86H142Q168 74 198 84L222 160H38Z" fill="#ece3cc" stroke="#2b1c11" stroke-width="2.5" stroke-linejoin="round"/>
    <path d="M90 80L114 96 130 160 146 96 170 80 184 160H76Z" fill="#1b1511" stroke="#0a0604" stroke-width="2" stroke-linejoin="round"/>
    <path d="M114 98L130 130 146 98Z" fill="#f4ecd6"/>
    <circle cx="130" cy="142" r="2.4" fill="#c9a24a"/><circle cx="130" cy="152" r="2.4" fill="#c9a24a"/>
    <path d="M100 124Q120 140 146 130" stroke="#c9a24a" stroke-width="1.8" fill="none" stroke-linecap="round"/>
    <path d="M117 66V86Q130 94 143 86V66Z" fill="#c68f62" stroke="#2b1c11" stroke-width="2"/>
    <path d="M110 86L124 100 130 92 136 100 150 86 141 80H119Z" fill="#fbf5e3" stroke="#2b1c11" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M115 94L130 100 115 109ZM145 94L130 100 145 109Z" fill="#8f1d25" stroke="#3a0c10" stroke-width="1.2" stroke-linejoin="round"/>
    <rect x="126.5" y="95.5" width="7" height="9" rx="2.2" fill="#a8252e" stroke="#3a0c10" stroke-width="1.1"/>

    <ellipse cx="104" cy="50" rx="4.2" ry="7" fill="#c98f62" stroke="#2b1c11" stroke-width="1.8"/>
    <ellipse cx="156" cy="50" rx="4.2" ry="7" fill="#c98f62" stroke="#2b1c11" stroke-width="1.8"/>
    <ellipse cx="130" cy="46" rx="25.5" ry="29.5" fill="#dcab7e" stroke="#2b1c11" stroke-width="2.5"/>
    <path d="M104 41Q102 15 130 14Q158 15 156 41Q150 27 130 26Q110 27 104 41Z" fill="#17110d" stroke="#0a0604" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M101 36Q130 21 159 36L161 43Q130 32 99 43Z" fill="#3f9a69" fill-opacity=".92" stroke="#1d4d35" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M108 34Q130 26 152 34" stroke="#c6efd5" stroke-opacity=".65" stroke-width="1.6" fill="none" stroke-linecap="round"/>
    <path d="M114 46H126M134 46H146" stroke="#17110d" stroke-width="2.6" stroke-linecap="round"/>
    <g class="d-eyes"><ellipse cx="120" cy="51" rx="3.1" ry="2.5" fill="#1a1008"/><ellipse cx="140" cy="51" rx="3.1" ry="2.5" fill="#1a1008"/></g>
    <path d="M130 52Q126.5 60 131 62" stroke="#8d5f3c" stroke-width="1.8" fill="none" stroke-linecap="round"/>
    <path d="M115 65Q122 59 130 63 138 59 145 65 138 68 130 66.5 122 68 115 65Z" fill="#17110d"/>
    <path class="d-mouth" d="M123 72Q129 75 135 72" stroke="#7a2a22" stroke-width="2.2" fill="none" stroke-linecap="round"/>
    <g class="d-cigar">
      <rect x="132" y="67" width="31" height="7.4" rx="3.4" fill="#6b4220" stroke="#2b1c11" stroke-width="1.4" transform="rotate(-14 132 70)"/>
      <rect x="132.8" y="68.4" width="9" height="4.6" rx="2" fill="#c9a24a" transform="rotate(-14 132 70)"/>
      <circle cx="162" cy="62.4" r="3.5" fill="#ff8a2a"/><circle cx="162" cy="62.4" r="1.6" fill="#ffd27a"/>
    </g>
    <g class="d-wisp w1"><path d="M163 57q7-8 1-15" stroke="#efe8d6" stroke-opacity=".55" stroke-width="3.2" fill="none" stroke-linecap="round"/></g>
    <g class="d-wisp w2"><path d="M162 57q-6-9 2-17" stroke="#efe8d6" stroke-opacity=".45" stroke-width="2.6" fill="none" stroke-linecap="round"/></g>
  </g>
</svg>`;

export const DEALER_FRONT_SVG = `
<svg viewBox="0 0 260 230" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <g class="d-arm d-arm-l">
    <path d="M78 86L50 126 74 168" stroke="#2b1c11" stroke-width="21" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M78 86L50 126 74 168" stroke="#ece3cc" stroke-width="17" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M71 96L65 104" stroke="#8f1d25" stroke-width="17" fill="none"/>
    <path d="M69 160L74 168" stroke="#fbf5e3" stroke-width="19" stroke-linecap="round" fill="none"/>
    <circle cx="76" cy="177" r="10.5" fill="#dcab7e" stroke="#2b1c11" stroke-width="2.2"/>
  </g>
  <g class="d-arm d-arm-r">
    <path d="M182 86L210 126 186 168" stroke="#2b1c11" stroke-width="21" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M182 86L210 126 186 168" stroke="#ece3cc" stroke-width="17" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M189 96L195 104" stroke="#8f1d25" stroke-width="17" fill="none"/>
    <path d="M191 160L186 168" stroke="#fbf5e3" stroke-width="19" stroke-linecap="round" fill="none"/>
    <circle cx="184" cy="177" r="10.5" fill="#dcab7e" stroke="#2b1c11" stroke-width="2.2"/>
  </g>
</svg>`;

/** Muted, period-appropriate portrait colours for players without a Discord picture. */
export const AVATAR_COLORS = ["#d9b36a", "#c8705b", "#93b07e", "#7a9cc0", "#b890b8", "#d99560"];
export function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}
